import { useCallback, useEffect, useState } from 'react';
import type { ApiResponse } from '@/types/api.type';
import api from '@/lib/api';

export interface ReportDataConfig<TSearchParams = any> {
  endpoint: string;
  useMock?: boolean;
  mockDataGenerator?: () => any[];
  searchParams?: TSearchParams;
  autoFetch?: boolean;
}
export function useReportData<TData, TSearchParams = any>(config: ReportDataConfig<TSearchParams>) {
  const [data, setData] = useState<TData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { searchParams } = config;
  const fetchData = useCallback(
    async (searchParams: TSearchParams) => {
      setIsLoading(true);

      try {
        let response;

        if (config.useMock && config.mockDataGenerator) {
          const mockData = config.mockDataGenerator();

          response = await api.get<ApiResponse<TData>>(config.endpoint, {
            mock: true,
            mockData,
            params: searchParams
          });
        } else {
          response = await api.get<ApiResponse<TData>>(config.endpoint, {
            params: searchParams
          });
        }
        setData(response.data.results);
      } catch (err) {
        setData([]);
      } finally {
        setIsLoading(false);
      }
    },
    [config]
  );

  const refreshData = useCallback(async () => {
    if (searchParams) {
      await fetchData(searchParams);
    }
  }, [fetchData, searchParams]);

  useEffect(() => {
    if (searchParams) {
      fetchData(searchParams);
    }
  }, []);

  return {
    data,
    isLoading,
    fetchData,
    refreshData
  };
}
